/**
 * In-app replacement for window.confirm / window.alert.
 *
 * Why: in the Electron desktop app on Windows, closing a native confirm/alert
 * leaves the page unable to focus text fields until the window loses and
 * regains focus (Alt+Tab). Opening an estimate went through exactly such a
 * confirm, so the estimate came up but nothing could be typed. A dialog drawn
 * by the page itself never hands focus to the OS, so the bug can't happen.
 *
 * Usage: const dialog = useAppDialog(); ... if (!(await dialog.confirm({...})))
 * return; ... and render {dialog.element} once. confirm/alert are stable across
 * renders, so listeners registered once can call them.
 */
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

export type AppDialogOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** What Escape answers. Default: Cancel (false) for confirm, OK for alert.
   *  Set true when the primary button is the choice that loses nothing. */
  escapeResult?: boolean;
};

type Pending = AppDialogOptions & {
  id: number;
  kind: "confirm" | "alert";
  resolve: (ok: boolean) => void;
};

export function useAppDialog() {
  // A queue, so a second request while one is showing waits its turn instead
  // of replacing it (which would leave the first promise unresolved forever).
  const [queue, setQueue] = useState<Pending[]>([]);
  const nextIdRef = useRef(1);

  const confirm = useCallback(
    (opts: AppDialogOptions) =>
      new Promise<boolean>((resolve) => {
        const id = nextIdRef.current++;
        setQueue((q) => [...q, { ...opts, id, kind: "confirm", resolve }]);
      }),
    []
  );

  const alert = useCallback(
    (opts: AppDialogOptions) =>
      new Promise<void>((resolve) => {
        const id = nextIdRef.current++;
        setQueue((q) => [
          ...q,
          { ...opts, id, kind: "alert", resolve: () => resolve() },
        ]);
      }),
    []
  );

  const current = queue[0] || null;
  const close = useCallback(
    (ok: boolean) => {
      if (!current) return;
      // By id, so a double click can't also dismiss the next queued dialog.
      setQueue((q) => (q[0]?.id === current.id ? q.slice(1) : q));
      current.resolve(ok);
    },
    [current]
  );

  const element = current ? (
    <AppDialog key={current.id} pending={current} onClose={close} />
  ) : null;

  return { confirm, alert, element };
}

function AppDialog({
  pending,
  onClose,
}: {
  pending: Pending;
  onClose: (ok: boolean) => void;
}) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Remember what had focus so it gets it back afterwards (when it is still
    // on the page — an open usually replaces the form underneath).
    const previous = document.activeElement as HTMLElement | null;
    primaryRef.current?.focus();
    return () => {
      if (previous && previous.isConnected) previous.focus?.();
    };
  }, []);

  const isConfirm = pending.kind === "confirm";
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose(pending.escapeResult ?? !isConfirm);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="du-modal-overlay"
      role={isConfirm ? "alertdialog" : "dialog"}
      aria-modal="true"
      aria-labelledby="app-dialog-title"
      onKeyDown={onKeyDown}
    >
      <div className="du-modal-card">
        <div className="du-modal-title" id="app-dialog-title">
          {pending.title}
        </div>
        {pending.message && (
          <div className="du-modal-subtitle" style={{ whiteSpace: "pre-line" }}>
            {pending.message}
          </div>
        )}
        <div className="du-modal-actions">
          {isConfirm ? (
            <button
              type="button"
              className="du-btn du-btn-ghost"
              onClick={() => onClose(false)}
            >
              {pending.cancelLabel || "Cancel"}
            </button>
          ) : (
            <span />
          )}
          <div className="du-modal-actions-right">
            <button
              ref={primaryRef}
              type="button"
              className="du-btn du-btn-primary"
              onClick={() => onClose(true)}
            >
              {pending.confirmLabel || "OK"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
